<template>
	<section class="marketing-section bg-color-gray-80">
		<div class="container text-align-center pb-300 pb-500-md">
			<div class="col-12 col-8-md col-offset-2-md">
				<p class="label-sm text-transform-uppercase text-color-gray-20">Our strategies</p>
				<h2 class="heading-xl text-color-white my-100"
					>Designed to harness the market's{{ '\xa0' }}potential</h2
				>
				<p class="body-xl text-color-white"
					>By employing a combination of strategies, we aim to build well-rounded, resilient portfolios
					targeted to deliver consistently strong results based on our clients' goals and appetite for
					risk.</p
				>
				<ul
					class="display-flex-md justify-content-space-around gutters-150-md text-align-center pt-300 pt-500-md pb-200 pb-300-md"
				>
					<li class="col-4-md pt-150-smo pb-200-smo display-flex flex-direction-column">
						<span class="heading-1 display-block text-color-white">
							{{ activeProjectCount }}
						</span>
						<span class="body-lg text-color-gray-20"> Active projects </span>
					</li>
					<li class="col-4-md pt-100-smo pb-200-smo display-flex flex-direction-column">
						<span class="heading-1 display-block text-color-white">
							{{ completedProjectCount }}
						</span>
						<span class="body-lg text-color-gray-20"> Completed projects </span>
					</li>

					<li class="col-4-md pt-100-smo display-flex flex-direction-column">
						<span class="heading-1 display-block text-color-white">
							{{ assetsTransactedStat }}
						</span>
						<span class="body-lg text-color-gray-20"> Total portfolio value* </span>
					</li>
				</ul>
				<p class="text-align-center text-color-gray-20 body-xs">
					<BaseLinkButton classes="text-color-gray-20 text-decoration-underline" @click="openModal"
						>Learn more</BaseLinkButton
					>
					about the assumptions in this section, or view our
					<BaseLink :link="performanceDisclosure" classes="text-color-gray-20 text-decoration-underline" />.
				</p>
			</div>
		</div>
		<RealEstateStrategiesSection :section="props.strategiesSection" :show-first-element-top-border="true" />
	</section>
</template>
<script setup lang="ts">
import { PortfolioStats, SanitySocialStat } from 'types/sanity/marketing-components';
import { annualReturnsDisclosureModalProps } from '@constants/annual-returns-data';
import { computed } from 'vue';
import { CurrentModal } from 'types/modal';
import DisclaimerSimple from '@components/disclaimer/disclaimer-simple.vue';
import { performanceDisclosure } from '@constants/links';
import RealEstateStrategiesSection from '@components/marketing-site/real-estate-strategies-section.vue';
import { StrategiesSection } from 'types/real-estate-strategy';
import { useAppStore } from '@stores/app';

interface Props {
	portfolioStats: PortfolioStats;
	strategiesSection: StrategiesSection;
}

const props = defineProps<Props>();

const appStore = useAppStore();

const annualReturnsDisclosureModal: CurrentModal = {
	modal: DisclaimerSimple,
	props: annualReturnsDisclosureModalProps
};

const hasReitTotals = computed((): boolean => {
	return props.portfolioStats?.reitsTotals?.strategySeries?.length > 0;
});

const activeProjectCount = computed((): string => {
	return hasReitTotals.value ? props.portfolioStats.reitsTotals.activeProjectCount : '--';
});

const completedProjectCount = computed((): string => {
	return hasReitTotals.value ? props.portfolioStats.reitsTotals.completedProjectCount : '--';
});

const assetsTransacted = computed((): SanitySocialStat | undefined =>
	props.portfolioStats.sanityStats?.statistics?.find((stat) => stat.slug.current === 'assetValue')
);

const assetsTransactedStat = computed((): string => {
	return assetsTransacted.value?.value ?? '--';
});

function openModal(): void {
	appStore.updateCurrentModal(annualReturnsDisclosureModal);
}
</script>
